import type { Post } from '../posts.js';

/**
 * Single row on the /blogs/ index page. Designed as a reading
 * list, not a marketing card grid — date eyebrow, large title,
 * 2-line description, mono meta footer. Whole row is clickable
 * via a stretched-link pattern so the click target is the full
 * card width even when the cursor sits on the description.
 *
 * The visible date uses ISO-like `YYYY-MM-DD` (no localization)
 * to stay consistent with the landing's terminal-flavored mono
 * accents (`~/memento`, `Local. Typed. Audited. Yours.`).
 */
export function PostCard({ post }: { readonly post: Post }): JSX.Element {
  return (
    <article className="group relative border-b border-border py-8 first:pt-0 md:py-10">
      <p className="font-mono text-xs uppercase tracking-widish text-muted">
        <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
      </p>
      <h2 className="mt-3 text-2xl font-medium leading-tight tracking-tight text-fg md:text-3xl">
        <a href={`/blogs/${post.slug}/`} className="after:absolute after:inset-0 hover:text-accent">
          {post.title}
        </a>
      </h2>
      <p className="mt-3 max-w-prose text-base leading-relaxed text-fg/75 md:text-lg">
        {post.description}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-xs uppercase tracking-widish text-muted">
        <span>{post.readingTime} min read</span>
        {post.tags.length > 0 ? <span aria-hidden>·</span> : null}
        {post.tags.map((tag, idx) => (
          <span key={tag}>
            <span>{tag}</span>
            {idx < post.tags.length - 1 ? <span aria-hidden> · </span> : null}
          </span>
        ))}
      </div>
    </article>
  );
}

function formatDate(iso: string): string {
  // US-English long-form ("May 17, 2026") to match the landing
  // footer's `dateModified` rendering. `timeZone: 'UTC'` pins
  // the result so SSR and client hydration agree regardless of
  // the visitor's local timezone — otherwise a `2026-05-17`
  // frontmatter date can display as "May 16, 2026" west of UTC.
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
